import type {
  BlackAndWhiteState,
  BlackAndWhiteViewerState,
  CompletedRound,
  RoundOutcome,
  TileColor,
  TileNumber,
  TileSubmission,
  ViewerCompletedRound,
  ViewerRoundInProgress,
  ViewerSubmission,
} from "../types";
import { BLACK_AND_WHITE_TILE_NUMBERS } from "../types";

export function isTileNumber(value: unknown): value is TileNumber {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    BLACK_AND_WHITE_TILE_NUMBERS.includes(value as TileNumber)
  );
}

export function getTileColor(tile: TileNumber): TileColor {
  return tile % 2 === 0 ? "BLACK" : "WHITE";
}

export function compareTiles(
  firstTile: TileNumber,
  secondTile: TileNumber,
): RoundOutcome {
  if (firstTile === secondTile) return "DRAW";
  if (firstTile === 1 && secondTile === 9) return "FIRST_WIN";
  if (firstTile === 9 && secondTile === 1) return "SECOND_WIN";
  return firstTile > secondTile ? "FIRST_WIN" : "SECOND_WIN";
}

export function getOtherPlayerId(
  playerIds: readonly string[],
  playerId: string,
): string {
  const otherPlayerId = playerIds.find((id) => id !== playerId);
  if (!otherPlayerId) {
    throw new Error("Black and White requires exactly two distinct players");
  }
  return otherPlayerId;
}

export function createSubmission(
  playerId: string,
  tile: TileNumber,
): TileSubmission {
  return { playerId, tile, color: getTileColor(tile) };
}

function redactSubmission(
  submission: TileSubmission,
  viewerPlayerId: string | null,
  revealAllTiles: boolean,
): ViewerSubmission {
  return {
    playerId: submission.playerId,
    color: submission.color,
    tile:
      revealAllTiles || submission.playerId === viewerPlayerId
        ? submission.tile
        : null,
  };
}

function redactCompletedRound(
  round: CompletedRound,
  viewerPlayerId: string | null,
  revealAllTiles: boolean,
): ViewerCompletedRound {
  return {
    ...round,
    firstSubmission: redactSubmission(
      round.firstSubmission,
      viewerPlayerId,
      revealAllTiles,
    ),
    secondSubmission: redactSubmission(
      round.secondSubmission,
      viewerPlayerId,
      revealAllTiles,
    ),
  };
}

export function filterStateForViewer(
  state: BlackAndWhiteState,
  viewerPlayerId: string | null,
): BlackAndWhiteViewerState {
  const knownViewerId = state.players.some(
    (player) => player.playerId === viewerPlayerId,
  )
    ? viewerPlayerId
    : null;
  const observerGodViewEnabled = state.observerGodViewEnabled === true;
  const revealSubmittedTiles =
    state.status === "COMPLETED" ||
    (knownViewerId === null && observerGodViewEnabled);
  const revealAllHands = knownViewerId === null && observerGodViewEnabled;

  const currentRound: ViewerRoundInProgress | null = state.currentRound
    ? {
        ...state.currentRound,
        firstSubmission: state.currentRound.firstSubmission
          ? redactSubmission(
              state.currentRound.firstSubmission,
              knownViewerId,
              revealSubmittedTiles,
            )
          : null,
      }
    : null;

  return {
    gameId: state.gameId,
    hostPlayerId: state.hostPlayerId,
    observerGodViewEnabled,
    viewerPlayerId: knownViewerId,
    status: state.status,
    players: state.players.map((player) => {
      const revealHand = revealAllHands || player.playerId === knownViewerId;
      return {
        playerId: player.playerId,
        score: player.score,
        remainingTiles: revealHand ? player.remainingTiles : null,
        usedTiles: revealHand ? player.usedTiles : null,
        remainingTileCount: player.remainingTiles.length,
      };
    }),
    completedRounds: state.completedRounds.map((round) =>
      redactCompletedRound(round, knownViewerId, revealSubmittedTiles),
    ),
    currentRound,
    winnerId: state.winnerId,
    maxRounds: state.maxRounds ?? 9,
    targetScore: state.targetScore ?? 5,
  };
}

export function buildViewerStates(
  state: BlackAndWhiteState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, BlackAndWhiteViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const playerIds = new Set(state.players.map((player) => player.playerId));
  const observerEntries = [...new Set(observerUserIds)]
    .filter((observerUserId) => !playerIds.has(observerUserId))
    .map(
      (observerUserId) =>
        [observerUserId, filterStateForViewer(state, null)] as const,
    );

  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
